type ContentSource = 'mdx' | 'contentful' | 'none';

type VisibilityCondition = boolean | string;

interface PublishingCondition {
  publishedAt?: string;
  visible?: VisibilityCondition;
}

interface ManifestSource {
  source: ContentSource;
  file?: string;
  inlineCondition?: PublishingCondition;
}

interface ManifestVariant extends ManifestSource {
  when?: PublishingCondition;
}

type ManifestEntry =
  | ContentSource
  | {
      default?: ManifestSource;
      variants?: ManifestVariant[];
    };

interface ManifestVisibilitySwitch {
  default?: boolean;
  description?: string;
}

interface Manifest {
  version: number;
  visibility?: Record<string, ManifestVisibilitySwitch>;
  entries: Record<string, ManifestEntry>;
}

interface PublishingContext {
  effectivePublishedAt: string;
  visibility: Record<string, boolean>;
}

interface QueryLike {
  [key: string]: string | string[] | undefined;
}

interface ResolvedManifestEntry {
  publicSlug: string;
  source: ContentSource;
  fileSlug: string;
  payloadKey: string;
  markdownKey: string;
  inlineCondition?: PublishingCondition;
}

interface MdxCompileTarget {
  publicSlug: string;
  sourceFileSlug: string;
  payloadKey: string;
  markdownKey: string;
  inlineCondition?: PublishingCondition;
  inlineConditionState?: boolean;
}

const LATEST_PUBLISHED_AT = '9999-12-31';
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
// ISO 8601 instant that carries an explicit timezone (Z or ±hh:mm). We require
// the offset so a publishedAt always describes an absolute moment rather than a
// floating local time.
const INSTANT_PATTERN =
  /^\d{4}(?:-\d{2}){2}T\d{2}(?::\d{2}){1,2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;
const VARIANT_PAYLOAD_PREFIX = '_variants/';
// `true`/`false` as string visibility values would collide with the boolean
// forms once rendered into an artifact path (both become `visible-true`), so
// they are reserved — authors must use a real boolean or a named switch.
const RESERVED_VISIBILITY_VALUES = new Set(['true', 'false']);

// Parse a publishedAt value into an absolute epoch-millisecond instant. Accepts
// either a bare calendar date (`YYYY-MM-DD`, interpreted as that day's UTC
// midnight) or a full ISO 8601 instant with a timezone
// (e.g. `2026-07-17T16:00:00Z`). Throws on anything that isn't a real,
// timezone-anchored moment so gate comparisons are always numeric — never a
// fragile lexicographic string compare — and invalid calendar dates like
// `2026-13-40` are rejected up front.
function parsePublishedAt(value: string): number {
  if (DATE_ONLY_PATTERN.test(value)) {
    const ms = Date.parse(`${value}T00:00:00.000Z`);
    // Round-trip guards both NaN and any silent rollover (e.g. 2026-02-30).
    if (Number.isNaN(ms) || new Date(ms).toISOString().slice(0, 10) !== value) {
      throw new Error(`publishedAt "${value}" is not a valid calendar date`);
    }
    return ms;
  }

  if (INSTANT_PATTERN.test(value)) {
    const ms = Date.parse(value);
    if (Number.isNaN(ms)) {
      throw new TypeError(`publishedAt "${value}" is not a valid instant`);
    }
    return ms;
  }

  throw new Error(
    'publishedAt must be a YYYY-MM-DD date or an ISO 8601 instant with a ' +
      `timezone (e.g. 2026-07-17T16:00:00Z); received "${value}"`
  );
}

function assertValidPublishedAt(value: string): void {
  parsePublishedAt(value);
}

// Path/URL-safe rendering of a publishedAt value. Date-only values pass through
// unchanged (`2026-07-17`); instants have `:` and any other filesystem/URL
// hostile character replaced so the generated artifact path stays one legal
// path segment (`2026-07-17T16:00:00Z` -> `2026-07-17T16-00-00Z`).
function publishedAtPathSegment(value: string): string {
  return value.replace(/[^\w.-]/g, '-');
}

function normalizeEntry(slug: string, entry: ManifestEntry | undefined) {
  if (!entry) {
    return {
      defaultSource: { source: 'contentful' as const },
      variants: [],
    };
  }

  if (typeof entry === 'string') {
    return {
      defaultSource: {
        source: entry,
        ...(entry === 'mdx' ? { file: slug } : {}),
      } as ManifestSource,
      variants: [],
    };
  }

  return {
    defaultSource: entry.default || ({ source: 'contentful' } as const),
    variants: entry.variants || [],
  };
}

function isVisible(
  visible: VisibilityCondition | undefined,
  context: PublishingContext
): boolean {
  if (visible === undefined) {
    return true;
  }
  if (typeof visible === 'boolean') {
    return visible;
  }
  return context.visibility[visible] === true;
}

function isConditionEligible(
  condition: PublishingCondition | undefined,
  context: PublishingContext
): boolean {
  if (!condition) {
    return true;
  }
  if (condition.publishedAt) {
    if (
      parsePublishedAt(condition.publishedAt) >
      parsePublishedAt(context.effectivePublishedAt)
    ) {
      return false;
    }
  }
  return isVisible(condition.visible, context);
}

// Absolute instant a variant becomes eligible, for ordering. Variants with no
// publishedAt are treated as always-published and sort first, so a dated
// variant always wins over an undated one when both are eligible.
function variantPublishedAtMs(variant: ManifestVariant): number {
  return variant.when?.publishedAt
    ? parsePublishedAt(variant.when.publishedAt)
    : Number.NEGATIVE_INFINITY;
}

// A visibility condition can be true at runtime unless it is the literal
// `false`. `undefined`/`true` are always visible; a named switch may be toggled
// on. Used to decide whether two variants could ever be eligible at once.
function isSatisfiableVisibility(
  visible: VisibilityCondition | undefined
): boolean {
  return visible !== false;
}

function isVariantPayloadKey(payloadKey: string): boolean {
  return payloadKey.startsWith(VARIANT_PAYLOAD_PREFIX);
}

function inlineConditionSelector(condition: PublishingCondition): string {
  const keys = [
    condition.publishedAt
      ? `publishedAt-${publishedAtPathSegment(condition.publishedAt)}`
      : '',
    condition.visible !== undefined ? `visible-${condition.visible}` : '',
  ].filter(Boolean);

  if (keys.length !== 1) {
    throw new Error(
      'inlineCondition must declare exactly one of publishedAt or visible'
    );
  }

  const key = keys[0];
  if (!key) {
    throw new Error('inlineCondition selector is empty');
  }
  return key;
}

function inlineVariantKey(
  basePayloadKey: string,
  condition: PublishingCondition
): string {
  return `_variants/inline-${inlineConditionSelector(
    condition
  )}/${basePayloadKey}`;
}

function variantPayloadKey(
  publicSlug: string,
  variant: ManifestVariant,
  index: number
): string {
  const parts: string[] = [];
  if (variant.when?.publishedAt) {
    parts.push(publishedAtPathSegment(variant.when.publishedAt));
  }
  if (typeof variant.when?.visible === 'string') {
    parts.push(`visible-${variant.when.visible}`);
  }
  if (typeof variant.when?.visible === 'boolean') {
    parts.push(`visible-${variant.when.visible}`);
  }

  const selector = parts.length > 0 ? parts.join('_') : `variant-${index + 1}`;
  return `_variants/${selector}/${publicSlug}`;
}

interface ValidateManifestEntryOptions {
  // Names declared under `manifest.visibility`. When provided, any string
  // `visible` value must reference one of these switches.
  visibilityKeys?: Set<string>;
}

function validateVisibility(
  slug: string,
  label: string,
  visible: VisibilityCondition | undefined,
  options: ValidateManifestEntryOptions
): void {
  if (typeof visible !== 'string') {
    return;
  }
  if (RESERVED_VISIBILITY_VALUES.has(visible.toLowerCase())) {
    throw new Error(
      `manifest entry "${slug}" ${label} uses the reserved visibility value ` +
        `"${visible}"; use a boolean (true/false) or a named visibility switch`
    );
  }
  if (options.visibilityKeys && !options.visibilityKeys.has(visible)) {
    throw new Error(
      `manifest entry "${slug}" ${label} references visibility switch ` +
        `"${visible}" which is not declared in manifest.visibility`
    );
  }
}

function validateCondition(
  slug: string,
  label: string,
  condition: PublishingCondition,
  options: ValidateManifestEntryOptions
): void {
  if (condition.publishedAt !== undefined) {
    parsePublishedAt(condition.publishedAt);
  }
  validateVisibility(slug, label, condition.visible, options);
}

function validateSource(
  slug: string,
  label: string,
  source: ManifestSource,
  options: ValidateManifestEntryOptions
): void {
  // Only mdx sources read a file or expand inline conditions; flag config that
  // silently wouldn't take effect.
  if (source.source !== 'mdx') {
    if (source.file !== undefined) {
      throw new Error(
        `manifest entry "${slug}" ${label} declares a file but its source is ` +
          `"${source.source}"; only mdx sources read a file`
      );
    }
    if (source.inlineCondition !== undefined) {
      throw new Error(
        `manifest entry "${slug}" ${label} declares an inlineCondition but ` +
          `its source is "${source.source}"; inline conditions only apply to ` +
          'mdx sources'
      );
    }
  }
  if (source.inlineCondition) {
    validateCondition(
      slug,
      `${label} inlineCondition`,
      source.inlineCondition,
      options
    );
  }
}

function validateManifestEntry(
  slug: string,
  entry: ManifestEntry,
  options: ValidateManifestEntryOptions = {}
): void {
  const { defaultSource, variants } = normalizeEntry(slug, entry);

  validateSource(slug, 'default', defaultSource, options);

  // Group satisfiable variants by their absolute publishedAt instant. Two
  // variants that share an instant *and* can both be visible at once would let
  // manifest ordering silently pick the winner (`.at(-1)` after a stable
  // sort), so reject that overlap up front and make the author disambiguate
  // with distinct instants or non-overlapping visibility.
  const satisfiableByInstant = new Map<number, number>();
  variants.forEach((variant) => {
    validateSource(slug, 'variant', variant, options);
    if (variant.when) {
      validateCondition(slug, 'variant when', variant.when, options);
    }
    if (!isSatisfiableVisibility(variant.when?.visible)) {
      return;
    }
    const instant = variantPublishedAtMs(variant);
    const count = (satisfiableByInstant.get(instant) || 0) + 1;
    if (count > 1) {
      throw new Error(
        `manifest entry "${slug}" has overlapping variants that can be ` +
          'eligible at the same time; give them distinct publishedAt instants ' +
          'or non-overlapping visibility conditions'
      );
    }
    satisfiableByInstant.set(instant, count);
  });
}

function validateManifest(manifest: Manifest): void {
  const visibilityKeys = new Set(Object.keys(manifest.visibility || {}));
  for (const [slug, entry] of Object.entries(manifest.entries)) {
    validateManifestEntry(slug, entry, { visibilityKeys });
  }
}

export {
  LATEST_PUBLISHED_AT,
  assertValidPublishedAt,
  inlineVariantKey,
  isConditionEligible,
  isVariantPayloadKey,
  normalizeEntry,
  parsePublishedAt,
  validateManifest,
  validateManifestEntry,
  variantPayloadKey,
  variantPublishedAtMs,
};
export type {
  ContentSource,
  Manifest,
  ManifestEntry,
  ManifestSource,
  ManifestVariant,
  MdxCompileTarget,
  PublishingCondition,
  PublishingContext,
  QueryLike,
  ResolvedManifestEntry,
  VisibilityCondition,
};
